import { useEffect } from "react";
import {
  ColorPicker,
  Divider,
  Input,
  Button,
  Tooltip,
  Collapse,
  DatePicker,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  AimOutlined,
  GlobalOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import styled from "styled-components";
import { useCustomMarkers } from "../../../hooks/useCustomMarkers";
import { useConfirmModal } from "../../../hooks/useConfirmModal";

export interface MarkerGroupRowProps {
  id: number;
  color: string;
  name: string;
  onColorChange: (hex: string) => void;
  onNameChange: (name: string) => void;
  onRemove: () => void;
}

const RowWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
  flex-direction: column;
`;

const NameRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
`;

const IconButton = styled(Button)`
  color: #eeeeee !important;
  background: #393e46 !important;
  border: none !important;
  border-radius: 8px;
  flex-shrink: 0;

  &:hover {
    color: #8e1616 !important;
    background: #222831 !important;
  }
  &.ant-btn-dangerous {
    color: #eeeeee !important;
  }
  &.ant-btn-dangerous:hover {
    color: #8e1616 !important;
  }
`;

const PickerContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
`;

const NameInput = styled(Input)`
  background: #393e46 !important;
  border-color: #393e46 !important;
  color: #eeeeee !important;
  border-radius: 8px;

  &:hover,
  &:focus {
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2) !important;
  }

  &::placeholder {
    color: #666 !important;
  }
`;

const PickerLabel = styled.span<{ $hidden: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  font-size: 14px;
  color: #eeeeee;
  pointer-events: none;
  user-select: none;
  text-align: center;
  width: 100%;
  text-decoration: ${({ $hidden }) => ($hidden ? "line-through" : "none")};
`;

const WideColorPicker = styled(ColorPicker)`
  width: 100%;
  flex: 1;

  .ant-color-picker-color-block {
    width: 100%;
  }

  .ant-color-picker-trigger {
    background: #eeeeee;
    border-color: #222831;
  }

  .ant-color-picker-trigger:hover {
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2);
  }
`;

const BottomDivider = styled(Divider)<{ $color: string }>`
  width: 100%;
  margin-top: 4px;
  margin-bottom: 12px;
  &.ant-divider-horizontal {
    border-top: 4px solid ${(p) => p.$color} !important;
    border-radius: 2px;
  }
`;

const StyledCollapse = styled(Collapse)`
  background: #2e3440 !important;
  border: 1px solid #393e46 !important;
  border-radius: 8px !important;

  .ant-collapse-header {
    color: #aaaaaa !important;
    font-size: 12px;
    padding: 6px 12px !important;
  }

  .ant-collapse-content {
    background: #2e3440 !important;
    border-top: 1px solid #393e46 !important;
  }

  .ant-collapse-content-box {
    padding: 12px !important;
  }
`;

const FormInput = styled(Input)`
  background: #393e46 !important;
  border-color: #393e46 !important;
  color: #eeeeee !important;
  border-radius: 8px;

  &:hover,
  &:focus {
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2) !important;
  }

  &::placeholder {
    color: #666 !important;
  }
`;

const FormTextArea = styled(Input.TextArea)`
  background: #393e46 !important;
  border-color: #393e46 !important;
  color: #eeeeee !important;
  border-radius: 8px;
  resize: unset !important;
  min-height: 100px !important;
  max-height: 300px !important;

  &::-webkit-scrollbar-track {
    background: #393e46;
    width: 4px;
    border-radius: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #393e46;
    border-radius: 8px;
  }

  &:hover,
  &:focus {
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2) !important;
  }

  &::placeholder {
    color: #666 !important;
  }
`;

const DatePickerWrapper = styled.div`
  width: 100%;

  .ant-picker {
    width: 100%;
    background: #393e46;
    border-color: #222831;
    color: #eeeeee;
  }
  .ant-picker-input > input {
    color: #eeeeee;
    caret-color: #eeeeee;
  }
  .ant-picker-input > input::placeholder {
    color: #eeeeee;
    opacity: 0.7;
  }
  .ant-picker-suffix .anticon svg,
  .ant-picker-clear .anticon svg {
    fill: #eeeeee;
    color: #eeeeee;
  }
  .ant-picker:hover,
  .ant-picker-focused {
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2) !important;
  }
`;

const AddMarkerButton = styled(Button)`
  border-radius: 8px;
  color: #eeeeee !important;
  background: #393e46 !important;
  width: 100%;
  &:hover {
    color: #8e1616 !important;
    background: #222831 !important;
    border-color: #8e1616 !important;
  }
`;

export default function MarkerGroupRow({
  id,
  color,
  name,
  onColorChange,
  onNameChange,
  onRemove,
}: MarkerGroupRowProps) {
  const { groups, addMarker, removeMarker, updateMarker, setGroupVisible } =
    useCustomMarkers();
  const { confirm } = useConfirmModal();
  const group = groups.find((g) => g.id === id);
  const markers = group?.markers ?? [];
  const isVisible = group?.visible !== false;

  const handleJumpToMarkers = () => {
    window.dispatchEvent(
      new CustomEvent("fit-markers-for-row", { detail: { rowId: -(id + 1) } }),
    );
  };

  const pickCoordinate = (markerId: string) => {
    window.dispatchEvent(
      new CustomEvent("pick-coordinate-start", { detail: { markerId } }),
    );
  };

  useEffect(() => {
    const handler = (e: Event) => {
      const { markerId, lat, lng } = (e as CustomEvent).detail;
      updateMarker(id, markerId, "latitude", String(lat));
      updateMarker(id, markerId, "longitude", String(lng));
    };
    window.addEventListener("pick-coordinate-result", handler);
    return () => window.removeEventListener("pick-coordinate-result", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <RowWrapper>
      <PickerContainer>
        <PickerLabel $hidden={!isVisible}>{name || "Marker group"}</PickerLabel>
        <WideColorPicker
          value={color}
          onChange={(_, hex) => onColorChange(hex)}
        />
      </PickerContainer>

      <NameRow>
        <NameInput
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Group name"
        />
        <Tooltip title="Jump to markers">
          <IconButton
            icon={<GlobalOutlined />}
            type="text"
            onClick={handleJumpToMarkers}
          />
        </Tooltip>
        <Tooltip title={isVisible ? "Hide markers" : "Show markers"}>
          <IconButton
            icon={isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            type="text"
            onClick={() => setGroupVisible(id, !isVisible)}
          />
        </Tooltip>
        <Tooltip title="Remove group">
          <IconButton
            icon={<DeleteOutlined />}
            type="text"
            danger
            onClick={() =>
              confirm({
                question: `Are you sure you want to remove the group "${name || "Marker group"}"?`,
                onOk: onRemove,
              })
            }
          />
        </Tooltip>
      </NameRow>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {markers.map((m, i) => (
          <StyledCollapse
            key={m.id}
            ghost={false}
            items={[
              {
                key: "marker",
                label: (
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <span>{m.name || `Marker ${i + 1}`}</span>
                    <span style={{ display: "flex", gap: 4 }}>
                      <Tooltip title="Pick coordinate on map">
                        <IconButton
                          icon={<AimOutlined />}
                          type="text"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            pickCoordinate(m.id);
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Remove marker">
                        <IconButton
                          icon={<DeleteOutlined />}
                          type="text"
                          size="small"
                          danger
                          onClick={(e) => {
                            e.stopPropagation();
                            confirm({
                              question: `Are you sure you want to remove "${m.name || `Marker ${i + 1}`}"?`,
                              onOk: () => removeMarker(id, m.id),
                            });
                          }}
                        />
                      </Tooltip>
                    </span>
                  </span>
                ),
                children: (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    <div style={{ display: "flex", gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <FormInput
                          value={m.name}
                          onChange={(e) =>
                            updateMarker(id, m.id, "name", e.target.value)
                          }
                          placeholder="Marker name"
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <DatePickerWrapper>
                          <DatePicker
                            value={m.date}
                            onChange={(v: Dayjs | null) =>
                              updateMarker(id, m.id, "date", v)
                            }
                            placeholder="Pick a date"
                          />
                        </DatePickerWrapper>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <FormInput
                          value={m.latitude}
                          onChange={(e) =>
                            updateMarker(id, m.id, "latitude", e.target.value)
                          }
                          placeholder="Latitude"
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <FormInput
                          value={m.longitude}
                          onChange={(e) =>
                            updateMarker(id, m.id, "longitude", e.target.value)
                          }
                          placeholder="Longitude"
                        />
                      </div>
                    </div>
                    <FormTextArea
                      value={m.description}
                      onChange={(e) =>
                        updateMarker(id, m.id, "description", e.target.value)
                      }
                      placeholder="Description"
                      rows={2}
                    />
                  </div>
                ),
              },
            ]}
          />
        ))}
        <AddMarkerButton
          icon={<PlusOutlined />}
          type="dashed"
          onClick={() => addMarker(id)}
        >
          Add marker
        </AddMarkerButton>
      </div>
      <BottomDivider $color={color} />
    </RowWrapper>
  );
}
